/**
 * The honesty panel.
 *
 * This is shown on first run and reachable at any time, and it is not boilerplate. A
 * demand map that gets trusted as an earnings map will send you into a square with
 * forty other couriers in it. The blind spot has to be stated plainly, at the front,
 * or the tool does harm.
 */

import type { BundleMeta } from '@tlv/shared';

interface Props {
  meta: BundleMeta;
  onClose: () => void;
}

export function AboutSheet({ meta, onClose }: Props): JSX.Element {
  const built = new Date(meta.builtAt);

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true" aria-label="About this map">
      <div className="sheet">
        <h2>What this map is</h2>
        <p>
          It predicts <strong>where orders are being created</strong> across Tel Aviv-Yafo,
          Givatayim and Ramat Gan — from open-venue density, what kind of food each place
          sells, how long each is from you on an ebike, and where people live and work.
        </p>

        <h2>What it cannot see</h2>
        <p>
          <strong>How many other couriers are already there.</strong> What you earn is
          demand divided by the number of riders competing for it, and only Wolt can see
          that second number.
        </p>
        <p className="emphasis">
          So use this to find demand, then check it against Wolt's own hotspot overlay —
          which is effectively a map of where couriers already are. The cells worth
          riding to are the ones that score well here and are <em>not</em> lit up there.
        </p>

        <h2>How good is it?</h2>
        <p>
          Honestly: unproven. The weights are informed estimates, not measurements, and
          with no record of what you actually earn there is no way to score the model
          objectively. Expect the first version to be mediocre. Ride with it, notice
          where it is wrong, and tune <code>weights.ts</code>.
        </p>

        <h2>If you lose signal</h2>
        <p>
          The rankings keep working — the venue data and the model live on this device.
          The map underneath will not: Google&rsquo;s terms don&rsquo;t allow storing
          their tiles, so there is nothing cached to fall back on. You lose the streets,
          not the answer.
        </p>

        {meta.synthetic && (
          <div className="warning-box">
            <strong>This is synthetic data.</strong> The venues on this map are
            plausible inventions shaped like Tel Aviv, not real places. Run{' '}
            <code>npm run pipeline:catalogue</code> on a machine with internet access to
            replace it with the real city.
          </div>
        )}

        <dl className="about-stats">
          <div>
            <dt>Venues</dt>
            <dd>{meta.venueCount.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Cells</dt>
            <dd>{meta.cellCount.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Known hours</dt>
            <dd>{Math.round(meta.hoursCoverage * 100)}%</dd>
          </div>
          <div>
            <dt>Data built</dt>
            <dd>{built.toLocaleDateString()}</dd>
          </div>
        </dl>

        {meta.hoursCoverage < 0.5 && (
          <p className="caveat">
            Under half the venues publish opening hours, so the rest are estimated from
            what similar venues nearby do. That estimate is weakest exactly where it
            matters most — late night and Shabbat.
          </p>
        )}

        <button type="button" className="primary-button" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );
}
